import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { updateEngageMessageById } from 'api/engage';
import { LetterDraftFormValues } from 'assets/js/pages/engage/letters/types';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

const useLetterDraftSubmit = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const methods = Form.useFormContext<LetterDraftFormValues>();
    const { setError } = methods;

    return useMutation({
        mutationFn: (_: LetterDraftFormValues) => {
            return updateEngageMessageById(accountId, letter!.id, {
                channel: 'letter',
                status: 'queued',
            });
        },
        onSuccess: () => {
            toast.success('Created letter successfully!');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessages(accountId),
                type: 'all',
            });
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;
            const errors = data?.errors;

            toast.error(data?.message ?? 'An unknown error occurred');

            if (typeof errors === 'object') {
                for (let field in errors) {
                    setError(field as keyof LetterDraftFormValues, {
                        message: errors[field][0],
                        type: 'server',
                    });
                }
            }
        },
    });
};

export { useLetterDraftSubmit };
